import React, { FC, useEffect, useState, useCallback } from 'react';
import Input from 'components/atoms/TextField';
import Button from 'components/atoms/Button';
import FilterSet from 'components/molecules/FilterSet';
import MapLegends from 'components/molecules/MapLegends';
import addressValidate from 'services/experian.service';
import SimpleBar from 'simplebar-react';
import 'styles/sidebar.scss';
import { useDispatch, useSelector } from 'react-redux';
import * as SearchSlice from 'store/slice/search.slice';
import { asrList } from 'services/asr.service';
import type { RootState } from 'store';
import { SideBarType, REOptionType, CategoryType } from 'types/molecules.types';
import { debounce } from 'lodash';
import { asrListInitial } from "services/asr.service";
import CONSTANTS from 'constants/constants';
import * as Icons from 'constants/icons';

const Sidebar: FC<SideBarType> = ({ isDesktop }) => {
  const { searchData, configs, isEdit, errors, loading } = useSelector(
    (state: RootState) => state.search
  );
  const dispatch = useDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [addressData, setAddressData] = useState([] as any);
  const [filterRequiredExpertise, setFilterRequiredExpertise] = useState([]);

  useEffect(() => {
    if (searchData?.category) {
      const isAll = searchData?.category.some(
        (category: any) => category.name === 'All'
      );
      const uniqueNamesFilter: any = configs.required_expertises.map(ele => ele['name'])      
      setFilterRequiredExpertise(
        isAll
          ? configs.required_expertises.filter(({name}, index) => !uniqueNamesFilter.includes(name, index + 1))
          : configs.required_expertises.filter((category: REOptionType) =>
              searchData?.category.some(
                (label: any) => label.id === category.categoryId
              )
            )
      );
    }
  }, [configs.required_expertises, searchData.category]);

  const getAddress = useCallback(
    debounce((keyWord) => {
      if (keyWord.length > CONSTANTS.CONFIG.SUGGESTION_CHAR_LIMIT) {
        addressValidate(keyWord).then((res) => {
          if (res?.results && res.results.length > 0) {
            dispatch(SearchSlice.setErrors({address: ''}))
            setAddressData(res.results);
          } else {
            dispatch(SearchSlice.setErrors({address: CONSTANTS.CONFIG.MOLECULES.MESSAGES.ADDRESS_ERROR}));
            setAddressData([]);
          }
        });
      } else {
        setAddressData([]);
        dispatch(SearchSlice.setErrors({address: ''}));
      }
    }, 300),
    []
  );

  const handleOnChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const keyValue = name === 'time' ? repairTime(value, e) : value;
    dispatch(
      SearchSlice.setSearchData({
        ...searchData,
        [name]: keyValue,
      })
    );
    if (name === 'search') {
      getAddress(value);
    }
  };

  const handleOnSetInitialValue = () => {
    if (searchData.time !== 'undefined' && (searchData.time === '' || Number(searchData.time) === 0 )) {
      dispatch(SearchSlice.setSearchData({
        ...searchData, time: configs.time
      }))
    }
  }

  const repairTime = (value: string, e: any) => {
    let result;
    result = value
      ?.replace(/[^\d.]/g, '')
      .replace('.', '$1')
      .replace(/\./, '')
      .replace('$1', '.')
      .slice(0, 5);

    const checkHourMin = result?.split('.');
    if (
      (checkHourMin &&
        checkHourMin?.length === 1 &&
        Number(checkHourMin[0]) > 23) ||
      (checkHourMin?.length === 2 && Number(checkHourMin[0]) > 23) ||
      Number(checkHourMin[1]) > 59
    ) {
      return configs?.time;
    }
    return result;
  };

  const handleOnCheckbox = (e: any, fieldName: string, optionsData: any) => {
    const { value, checked } = e.target;
    if (fieldName === 'category') {
      const isAll = searchData.category.some((ele: any) => ele.id === 'all' && !checked)
      const serviceAndInstal = searchData.category.some((ele: any) => ele.name === 'Service' || ele.name === 'Installation' )
      dispatch(
        SearchSlice.setSearchData({
          ...searchData,
          category: value === 'All' && checked ? configs.categories : serviceAndInstal && checked && value ? configs.categories : checked
            ? [
                ...searchData.category,
                optionsData.filter((item: CategoryType) => item.name === value)[0]
              ]
            : value === 'All' ? [] : isAll ? searchData.category.filter((ele: any) => ele.id !== 'all' && ele.name !== value) : searchData.category.filter((e: CategoryType) => e.name !== value),
        })
      );
    }
    if (fieldName === 'required_expertise') {
      dispatch(
        SearchSlice.setSearchData({
          ...searchData,
          required_expertise: checked
            ? [
                ...searchData.required_expertise,
                configs.required_expertises.filter(
                  (item: REOptionType) => item.id === value
                )[0],
              ]
            : searchData.required_expertise.filter((e: REOptionType) => e.id !== value),
        })
      );
    }
  };

  const setAsrListData = async () => {
    asrListInitial()
    .then((response: any) => {
      dispatch(
        SearchSlice.setASRData({
          asrList: response.data,
          latitude: response.latitude,
          longitude: response.longitude,
        })
      );
    })
    .catch((response: any) => console.log(response));
  }
    
  const handleOnClearData = () => {    
    dispatch(SearchSlice.setLoading(false))
    dispatch(SearchSlice.onClearSearchData());
    dispatch(SearchSlice.onClearData());
    dispatch(SearchSlice.setIsSearchBtnClicked(false));
    dispatch(SearchSlice.setIsEdit(false));
    dispatch(SearchSlice.setShowDrawer(false));
    if (!isDesktop) {
      dispatch(SearchSlice.setShowDrawer(true));
    }
    dispatch(SearchSlice.setErrors({address: '', noResult: ''}))
    setAsrListData();      
  };

  const handleOnEdit = () => {
    dispatch(SearchSlice.setIsEdit(false));
  };

  const handleOnRemoveItem = (id: string) => {
    if (searchData?.required_expertise.length === 1) {
      dispatch(SearchSlice.setIsEdit(false));
    }
    dispatch(
      SearchSlice.setSearchData({
        ...searchData,
        required_expertise: searchData.required_expertise.filter(
          (ele: REOptionType) => ele.id !== id
        ),
      })
    );
  };

  const handleOnSelect = (name: any, field: string) => {
    setIsMenuOpen(false);
    dispatch(
      SearchSlice.setSearchData({
        ...searchData,
        [field]: field === 'radius' ? name : name?.suggestion,
      })
    );
    setAddressData([]);
  };

  const handleOnInputFocus = (e: any) => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleCheckIsData = () => {
    if (searchData.search.length > 0 || searchData.radius.length > 0 || searchData.time.length > 0) {
      return false;
    }
    return true;
  }

  const customMap = (categories: CategoryType[]) => {
    return categories.map((ele: CategoryType) => ele.id) ?? []
  }

  const handleOnSubmit = async () => {
    let { required_expertise, category, ...data } = searchData
    const isAll = category.some((ele: CategoryType) => ele.id === 'all')
    const categoryList = isAll ? customMap(configs.categories).filter((item: string) => item !== 'all') : customMap(category)
    dispatch(SearchSlice.setLoading(true))
    if (categoryList.length === 0) {
      required_expertise = []
    }
    const filterData = {
      ...data,
      category: categoryList,
      required_expertise: required_expertise.length > 0 ? required_expertise.map((ele: any) => ele.SkillId) : []
    }
    const response = await asrList(filterData);
    if (response?.data && response.data.length > 0) {
      dispatch(SearchSlice.setErrors({noResult: ''}))
      dispatch(SearchSlice.setIsSearchBtnClicked(true));
      dispatch(SearchSlice.setShowDrawer(true));
      dispatch(
        SearchSlice.setASRData({
          asrList: response?.data,
          latitude: response?.latitude,
          longitude: response?.longitude,
        })
      );
    } else {
      dispatch(SearchSlice.setErrors({noResult: response?.error?.message }))
      dispatch(SearchSlice.setIsSearchBtnClicked(false));
      dispatch(SearchSlice.setShowDrawer(false));
      dispatch(SearchSlice.onClearData())
    }
    dispatch(SearchSlice.setLoading(false))
    if (searchData?.required_expertise.length > 0) {
      dispatch(SearchSlice.setIsEdit(true));
    }
  };

  useEffect(() => {
    window.addEventListener('click', (event: any) => {
      const radius = document.querySelector('.radius-field');
      setAddressData([]);
      if (!radius?.contains(event.target)) {
        setIsMenuOpen(false);
      }
    });
  }, [configs]);

  const heightStyle = isDesktop
    ? { height: 'calc(100vh - 330px)' }
    : { height: 'calc(100vh - 902px)' };

  return (
    <>
      <SimpleBar
        scrollbarMinSize={5}
        scrollbarMaxSize={205}
        autoHide={false}
        style={heightStyle}
      >
        <div className='asr-sidebar__content' id='asr-content'>
          {isDesktop && <h1>Find KOHLER Authorized Service Representative</h1>}
          <div className='required'>Fields marked with * are required.</div>
          <div className='asr-surface-control'>
            <Input
              inputId='search'
              value={searchData.search}
              onChange={handleOnChange}
              placeholder='Enter Address, City or Zip code'
              required
              dropdownOption={addressData}
              handleOnSelection={handleOnSelect}
              isMenuOpen='search'
              preFixIcon={Icons.search}
              message='Eg: W143 N9358 HENARY Stark Rd'
              errorMessage={errors.address}
            />
            <section className='rad-time-inp-grp'>
              <Input
                inputId='radius'
                value={searchData.radius}
                onChange={(e) => {
                  handleOnChange(e);
                }}
                placeholder='Radius'
                required
                dropdownOption={configs.radius}
                handleOnSelection={handleOnSelect}
                isInputfocus={handleOnInputFocus}
                isMenuOpen={isMenuOpen}
                sufFixIcon={Icons.arrowDown}
                isReadOnly={true}
              />
              <Input
                inputId='time'
                value={searchData.time}
                onChange={(e) => {
                  handleOnChange(e);
                }}
                placeholder='Est. Repair Time (h)'
                required
                onBlur={handleOnSetInitialValue}
              />
            </section>
          </div>
            <FilterSet
              title='Category'
              className='category'
              options={configs.categories}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleOnCheckbox(e, 'category', configs.categories)
              }
              selectedCheckboxData={searchData.category}
            />
          {filterRequiredExpertise.length !== 0 && (
            <FilterSet
              title='Required Expertise'
              className='required_expertise'
              options={filterRequiredExpertise}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleOnCheckbox(
                  e,
                  'required_expertise',
                  configs.required_expertises
                );
              }}
              selectedCheckboxData={searchData.required_expertise}
              onRemove={handleOnRemoveItem}
              isChip
              onEdit={handleOnEdit}
              isEdit={isEdit}
              desc='Select the expertise required from your KOHLER Authorized 
                Service Representative, Select as many as needed '
            />
          )}
        </div>
      </SimpleBar>

      <footer>
        {errors.noResult && <div className='error_no_result'>{errors.noResult}</div>}
        <div className='cta-grp'>
          <Button
            type='ghost'
            label='Clear'
            onClick={() => handleOnClearData()}
            disabled={handleCheckIsData()}
          />
          <Button
            type='primary'
            label='Search'
            onClick={() => handleOnSubmit()}
            loading={loading}
            disabled={
              !searchData.search || !searchData.radius || !searchData.time
            }
          />
        </div>
        <MapLegends />
      </footer>
    </>
  );
};

export default Sidebar;
